import React, { useEffect } from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface AIFeeCollectionPredictionProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const AIFeeCollectionPrediction: React.FC<AIFeeCollectionPredictionProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  useEffect(() => {
    document.title = 'AI Fee Collection & Default Prediction for Schools | My Learning Board';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'AI-powered fee collection system that predicts payment defaults, sends smart reminders via WhatsApp, and improves collection rates by 35%. Auto-reconciliation, installment tracking & parent portal.');
    }
    return () => { document.title = 'My Learning Board – AI-Powered School Management Software & ERP in India'; };
  }, []);

  const features = [
    { icon: 'bi-graph-down-arrow', title: 'Default Prediction', desc: 'AI analyzes payment history, family income patterns and seasonal trends to predict which parents will delay fees — 2 weeks in advance.', color: '#E53935' },
    { icon: 'bi-whatsapp', title: 'Smart Reminders', desc: 'Personalized WhatsApp reminders in the parent\'s language. Tone adjusts based on payment history — gentle for first-timers, firm for repeat defaulters.', color: '#28A745' },
    { icon: 'bi-pie-chart', title: 'Collection Analytics', desc: 'Real-time dashboard showing collection rate, pending amount, class-wise breakdowns, and month-over-month trends.', color: '#2D7DD2' },
    { icon: 'bi-arrow-repeat', title: 'Auto Reconciliation', desc: 'Payments via UPI, bank transfer, cash or card — AI matches them to the right student automatically. Zero manual reconciliation.', color: '#FB8C00' },
    { icon: 'bi-calendar2-range', title: 'Installment Plans', desc: 'AI recommends optimal installment schedules based on parent\'s payment capacity. Flexible plans improve collection by 25%.', color: '#6610F2' },
    { icon: 'bi-shield-check', title: 'Compliance & Receipts', desc: 'Auto-generated fee receipts, GST-compliant invoices, and audit-ready reports. Everything is digital and searchable.', color: '#D81B60' },
  ];

  const howItWorks = [
    { step: '1', title: 'Fee Structure Setup', desc: 'Configure fee heads, discounts, scholarships, and installment plans. AI suggests optimal structures based on your school type.', icon: 'bi-gear' },
    { step: '2', title: 'AI Analyzes Patterns', desc: 'Machine learning models study historical payment data, identify patterns in late payments, and build prediction models.', icon: 'bi-cpu' },
    { step: '3', title: 'Proactive Intervention', desc: 'Before due dates, AI sends personalized reminders. For predicted defaulters, it triggers early outreach via WhatsApp & SMS.', icon: 'bi-bell' },
    { step: '4', title: 'Track & Optimize', desc: 'Real-time dashboards show collection health. AI recommends policy changes to improve rates — like adjusting due dates or offering early-bird discounts.', icon: 'bi-graph-up' },
  ];

  const impactStats = [
    { metric: 'Collection Rate Improvement', value: '+35%', desc: 'Schools see 35% faster fee collection with AI reminders', icon: 'bi-trending-up', color: '#28A745' },
    { metric: 'Reduction in Defaults', value: '-40%', desc: 'Predictive alerts reduce fee defaults significantly', icon: 'bi-shield-check', color: '#2D7DD2' },
    { metric: 'Admin Time Saved', value: '80%', desc: 'No more manual follow-ups, calls and spreadsheet tracking', icon: 'bi-clock', color: '#FFC107' },
    { metric: 'Parent Satisfaction', value: '92%', desc: 'Flexible plans and WhatsApp updates keep parents happy', icon: 'bi-emoji-smile', color: '#E83E8C' },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #28A745 0%, #1B5E20 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-7">
              <span className="badge bg-white text-dark mb-3 px-3 py-2">
                <i className="bi bi-cash-coin me-2"></i>AI Fee Management
              </span>
              <h1 className="display-4 fw-bold mb-4">
                AI Predicts Fee Defaults Before They Happen
              </h1>
              <p className="lead mb-4 opacity-90">
                Stop chasing payments. Let AI predict which parents will default, send smart reminders,
                and auto-reconcile collections — improving your collection rate by 35%.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>Book Free Demo
                </button>
                <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
                  <i className="bi bi-play-circle me-2"></i>Try Free
                </button>
              </div>
            </div>
            <div className="col-lg-5 text-center mt-4 mt-lg-0">
              <div className="bg-white rounded-4 p-4 shadow-lg text-dark">
                <h6 className="fw-bold mb-3" style={{ color: '#1B5E20' }}>AI Fee Collection Impact</h6>
                <div className="row g-3">
                  {impactStats.map((stat, idx) => (
                    <div key={idx} className="col-6">
                      <div className="text-center p-2">
                        <i className={`bi ${stat.icon} fs-4 d-block mb-1`} style={{ color: stat.color }}></i>
                        <h4 className="fw-bold mb-0" style={{ color: stat.color }}>{stat.value}</h4>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>{stat.metric}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">AI-Powered Fee Collection Features</h2>
            <p className="text-muted lead">Every aspect of fee management enhanced with artificial intelligence</p>
          </div>
          <div className="row g-4">
            {features.map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: `${feature.color}15` }}>
                      <i className={`bi ${feature.icon} fs-4`} style={{ color: feature.color }}></i>
                    </div>
                    <h5 className="card-title">{feature.title}</h5>
                    <p className="card-text text-muted">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">How AI Fee Prediction Works</h2>
            <p className="text-muted lead">From setup to optimized collection in 4 steps</p>
          </div>
          <div className="row g-4">
            {howItWorks.map((item, idx) => (
              <div key={idx} className="col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-sm text-center p-4">
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3"
                    style={{ width: '60px', height: '60px', backgroundColor: '#28A74515' }}>
                    <i className={`bi ${item.icon} fs-4`} style={{ color: '#28A745' }}></i>
                  </div>
                  <div className="display-6 fw-bold mb-2" style={{ color: '#28A745', opacity: 0.3 }}>{item.step}</div>
                  <h5 className="fw-bold">{item.title}</h5>
                  <p className="text-muted small">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Real School Scenarios</h2>
          </div>
          <div className="row g-4">
            {[
              { title: 'Quarter-End Rush', desc: 'AI detects that 120 families typically pay in the last week. It sends early-bird discount offers 2 weeks before deadline, spreading payments evenly.', icon: 'bi-calendar-event' },
              { title: 'Repeat Defaulter', desc: 'A parent has delayed payments 3 times this year. AI flags them early, auto-sends a flexible installment plan option via WhatsApp.', icon: 'bi-person-exclamation' },
              { title: 'Scholarship Student', desc: 'Fee waiver applied automatically. Remaining balance calculated and communicated. No manual adjustment needed.', icon: 'bi-award' },
            ].map((item, idx) => (
              <div key={idx} className="col-lg-4">
                <div className="card h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #28A745' }}>
                  <div className="card-body p-4">
                    <i className={`bi ${item.icon} fs-3 mb-3 d-block`} style={{ color: '#28A745' }}></i>
                    <h5 className="fw-bold">{item.title}</h5>
                    <p className="text-muted">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Stop Chasing Payments. Let AI Do It.</h2>
          <p className="lead opacity-75 mb-4">See how AI can improve your fee collection rate</p>
          <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
            <i className="bi bi-telephone me-2"></i>Book Free Demo
          </button>
        </div>
      </section>

      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AIFeeCollectionPrediction;
